
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface FieldNews {
  id: string;
  title: string;
  content: string;
  mission_field: string;
  category: string;
  news_date: string;
  images: { id: string; image_url: string; display_order: number }[];
}

export default function FieldNews() {
  const [isVisible, setIsVisible] = useState(false);
  const [newsList, setNewsList] = useState<FieldNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState<FieldNews | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const { data: newsData, error: newsError } = await supabase
        .from('field_news')
        .select('*')
        .order('news_date', { ascending: false })
        .limit(6);

      if (newsError) throw newsError;

      const newsWithImages = await Promise.all(
        (newsData || []).map(async (news) => {
          const { data: images } = await supabase
            .from('field_news_images')
            .select('*')
            .eq('news_id', news.id)
            .order('display_order', { ascending: true });

          return { ...news, images: images || [] };
        })
      );

      setNewsList(newsWithImages);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGallery = (news: FieldNews, imageIndex: number = 0) => {
    setSelectedNews(news);
    setCurrentImageIndex(imageIndex);
  };

  const closeGallery = () => {
    setSelectedNews(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedNews && currentImageIndex < selectedNews.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  if (loading) {
    return (
      <section ref={sectionRef} id="field-news" className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center text-sm sm:text-base">로딩 중...</div>
        </div>
      </section>
    );
  }

  if (newsList.length === 0) {
    return (
      <section 
        ref={sectionRef}
        id="field-news" 
        className={`py-12 sm:py-16 md:py-24 bg-white transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#2F4F4F] rounded-lg sm:rounded-xl flex items-center justify-center">
                <i className="ri-newspaper-line text-lg sm:text-2xl text-white"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#2F4F4F]">
                Field News
              </h2>
            </div>
            <p className="text-xs sm:text-sm md:text-base text-gray-700 max-w-3xl leading-relaxed">
              선교 현장의 최신 소식을 실시간으로 전해드립니다.
            </p>
          </div>

          <div className="bg-[#F5F5F0] rounded-xl sm:rounded-[20px] p-8 sm:p-12 md:p-16 text-center shadow-lg">
            <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <i className="ri-image-add-line text-2xl sm:text-3xl md:text-4xl text-[#2F4F4F]"></i>
            </div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-[#2F4F4F] mb-3 sm:mb-4">
              콘텐츠 준비 중
            </h3>
            <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto leading-relaxed">
              선교 현장의 최신 소식과 사진이 곧 업로드됩니다.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section 
        ref={sectionRef}
        id="field-news" 
        className={`py-12 sm:py-16 md:py-24 bg-white transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#2F4F4F] rounded-lg sm:rounded-xl flex items-center justify-center">
                <i className="ri-newspaper-line text-lg sm:text-2xl text-white"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#2F4F4F]">
                Field News
              </h2>
            </div>
            <p className="text-xs sm:text-sm md:text-base text-gray-700 max-w-3xl leading-relaxed">
              선교 현장의 최신 소식을 실시간으로 전해드립니다. 여러분의 기도와 후원이 만들어낸 생생한 변화의 이야기들입니다.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {newsList.map((news) => (
              <div 
                key={news.id} 
                className="bg-white rounded-xl sm:rounded-[20px] overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
              >
                {news.images.length > 0 && (
                  <div 
                    className="w-full h-40 sm:h-48 md:h-64 cursor-pointer overflow-hidden"
                    onClick={() => openGallery(news, 0)}
                  >
                    <img
                      src={news.images[0].image_url}
                      alt={news.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                
                <div className="p-4 sm:p-5 md:p-6">
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    {news.mission_field && (
                      <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-[#2F4F4F] text-white rounded-full text-[10px] sm:text-xs font-medium">
                        {news.mission_field}
                      </span>
                    )}
                    {news.category && (
                      <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-[#F5F5F0] text-[#2F4F4F] rounded-full text-[10px] sm:text-xs font-medium">
                        {news.category}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-[#2F4F4F] mb-1.5 sm:mb-2 line-clamp-2">
                    {news.title}
                  </h3>
                  
                  {news.content && (
                    <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3">
                      {news.content}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] sm:text-xs text-gray-500">
                      {new Date(news.news_date).toLocaleDateString('ko-KR')}
                    </span>
                    
                    {news.images.length > 1 && (
                      <button
                        onClick={() => openGallery(news, 0)}
                        className="text-xs sm:text-sm text-[#2F4F4F] hover:text-[#1a2f2f] font-medium flex items-center gap-1 cursor-pointer"
                      >
                        <i className="ri-image-line"></i>
                        {news.images.length}장
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedNews && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={closeGallery}
        >
          <button
            onClick={closeGallery}
            className="absolute top-3 sm:top-4 right-3 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors z-10 cursor-pointer"
          >
            <i className="ri-close-line text-xl sm:text-2xl"></i>
          </button>

          <div 
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedNews.images[currentImageIndex].image_url}
                alt={selectedNews.title}
                className="w-full max-h-[60vh] sm:max-h-[80vh] object-contain rounded-lg"
              />

              {selectedNews.images.length > 1 && (
                <>
                  {currentImageIndex > 0 && (
                    <button
                      onClick={prevImage}
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/90 hover:bg-white rounded-full transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-xl sm:text-2xl text-gray-900"></i>
                    </button>
                  )}

                  {currentImageIndex < selectedNews.images.length - 1 && (
                    <button
                      onClick={nextImage}
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/90 hover:bg-white rounded-full transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-xl sm:text-2xl text-gray-900"></i>
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="mt-4 sm:mt-6 text-white">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-1.5 sm:mb-2">{selectedNews.title}</h3>
              {selectedNews.content && (
                <p className="text-sm sm:text-base text-gray-300 mb-3 sm:mb-4 line-clamp-3 sm:line-clamp-none">{selectedNews.content}</p>
              )}
              <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                <span>{new Date(selectedNews.news_date).toLocaleDateString('ko-KR')}</span>
                {selectedNews.images.length > 1 && (
                  <span>
                    {currentImageIndex + 1} / {selectedNews.images.length}
                  </span>
                )}
              </div>
            </div>

            {selectedNews.images.length > 1 && (
              <div className="flex gap-1.5 sm:gap-2 mt-3 sm:mt-4 overflow-x-auto pb-2">
                {selectedNews.images.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-md sm:rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      index === currentImageIndex
                        ? 'border-white'
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={image.image_url}
                      alt={`${selectedNews.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
